import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { Article, channel, CrossSelling, CustomerAlsoBoughtDetails, ProductGroup, UserModuleType } from "@tm/models"
import { useCallback, useMemo, useState } from "react"
import { getParameterFromUserContextModule, usePureArticleLists, useUser, useWorkTaskId } from "@tm/context-distribution"
import { Articles } from "@tm/data"
import { useQueryClient } from "react-query"
import { isSameArticle } from "@tm/utils"
import { getCachedWorkTaskBasketData } from "../../../../../../../basket/src/data/hooks/workTaskBasket/queries/useWorkTaskBasketData"
import {
    CrossSellingArticleState,
    CustomerAlsoBoughtProductGroupsState,
    CustomerAlsoBoughtArticleChainState,
    PartsViewSettingsState,
    RecommendedArticlesChainState,
    RecommendedProductGroupState,
} from "../../../states"
import { PureList } from "../../../../PureList/component"
import { useArticleListConfiguration } from "../../../ArticleListConfiguration"
import { useVehicle } from "../.."
import { useListParamsContext } from "../../../ContextProvider"
import { createRecommendedArticlesRequest, createUniversalSearchRequestPayload, createVehiclePartsRequestPayload } from "."
import { CustomerAlsoBoughtArticlesChain, RecommendedArticlesChain } from "../../../components/RecommendedArticles"
import { usePartsAlternativesModuleState } from "../../../PartsAlternatives/PartsAlternativesModuleState"
import { CrossSellingContext } from "../../../models"

export function useCrossSelling(article: Article): CrossSelling {
    const listId = "recommended-articles"
    const queryClient = useQueryClient()

    const vehicle = useVehicle()
    const workTaskId = useWorkTaskId()!
    const { userContext } = useUser()

    const { enableCrossSelling } = useArticleListConfiguration()
    const { listType } = useListParamsContext()
    const displayCrossSelling = listType === "vehicle" || listType === "universal"
    const partsViewSettings = useRecoilValue(PartsViewSettingsState)
    const quantitySuggestionEnabled = useMemo(() => partsViewSettings.quantitySuggestionEnabled, [partsViewSettings?.quantitySuggestionEnabled])

    const { reset, setRequest } = usePureArticleLists()
    const [referenceArticle, setReferenceArticle] = useRecoilState(CrossSellingArticleState)
    const [customerAlsoBoughtDetails, setCustomerAlsoBoughtDetails] = useState<CustomerAlsoBoughtDetails>({
        articles: undefined,
        productGroups: undefined,
        displayButton: undefined,
    })

    const setCustomerAlsoBoughtProductGroupsState = useSetRecoilState(CustomerAlsoBoughtProductGroupsState(workTaskId))
    const setRecommendedArticlesChain = useSetRecoilState(RecommendedArticlesChainState(workTaskId))
    const setCustomerAlsoBoughtArticlesChain = useSetRecoilState(CustomerAlsoBoughtArticleChainState(workTaskId))
    const [productGroups, setProductGroups] = useRecoilState(RecommendedProductGroupState(workTaskId))

    const [isLoading, setIsLoading] = useState(false)
    const context = getParameterFromUserContextModule(userContext, UserModuleType.CrossSelling, "Context") as CrossSellingContext
    const isAlternativeSearch = usePartsAlternativesModuleState((x) => !!x)

    const isEnabled = useMemo(
        () =>
            enableCrossSelling &&
            displayCrossSelling &&
            referenceArticle &&
            isSameArticle(referenceArticle, article) &&
            (!!productGroups?.recommendedProductGroupsState.length ||
                !!productGroups?.customerAlsoBoughtProductGroupsState?.length ||
                !!customerAlsoBoughtDetails?.articles?.length),
        [
            enableCrossSelling,
            displayCrossSelling,
            referenceArticle,
            article,
            productGroups?.recommendedProductGroupsState?.length,
            productGroups?.customerAlsoBoughtProductGroupsState?.length,
            customerAlsoBoughtDetails?.articles?.length,
        ]
    )

    const loadArticles = useCallback(
        async (newArticles: Article[]) => {
            const workTaskBasket = await getCachedWorkTaskBasketData(queryClient, workTaskId, true)

            const request = createRecommendedArticlesRequest(
                workTaskBasket?.parts,
                newArticles,
                workTaskId,
                setCustomerAlsoBoughtProductGroupsState,
                setRecommendedArticlesChain,
                setCustomerAlsoBoughtArticlesChain,
                referenceArticle && { ...referenceArticle, isSelected: true },
                vehicle
            )

            if (!request) {
                return undefined
            }

            const queryKey = [listId, JSON.stringify(request)]

            const response = await queryClient.fetchQuery({
                queryKey,
                queryFn: () => Articles.getRecommendedArticles(request),
                staleTime: 5 * 60 * 1000,
            })

            setProductGroups({
                recommendedProductGroupsState: response?.recommendedProductGroups || [],
                customerAlsoBoughtProductGroupsState: response?.customerAlsoBoughtProductGroups || [],
            })

            const updatedArticles =
                quantitySuggestionEnabled && response?.articles?.length
                    ? response.articles.map((a) => ({
                          ...a,
                          ...(a.suggestedQuantity && {
                              quantity: a.suggestedQuantity,
                              initialQuantity: a.suggestedQuantity,
                          }),
                      }))
                    : response?.articles

            return {
                articles: updatedArticles,
                customerAlsoBoughtProductGroups: response?.customerAlsoBoughtProductGroups ?? [],
                recommendedProductGroups: response?.recommendedProductGroups ?? [],
            }
        },
        [
            queryClient,
            workTaskId,
            setCustomerAlsoBoughtProductGroupsState,
            setRecommendedArticlesChain,
            setCustomerAlsoBoughtArticlesChain,
            referenceArticle,
            vehicle,
            setProductGroups,
            quantitySuggestionEnabled,
        ]
    )

    const loadRecommendedArticles = useCallback(async () => {
        if (!enableCrossSelling || isAlternativeSearch) {
            return
        }
        setReferenceArticle(article)

        setCustomerAlsoBoughtDetails({ articles: undefined, productGroups: undefined })

        const response = await loadArticles([{ ...article, isSelected: true }])

        if (!response?.articles?.length) {
            setCustomerAlsoBoughtArticlesChain(() => {
                localStorage.removeItem(`customerAlsoBought/${workTaskId}`)
                return []
            })
        }
        if (!response?.recommendedProductGroups?.length) {
            setRecommendedArticlesChain(() => {
                localStorage.removeItem(`recommendedArticles/${workTaskId}`)
                return []
            })
        }
        if (!response?.customerAlsoBoughtProductGroups?.length) {
            setCustomerAlsoBoughtProductGroupsState(() => {
                localStorage.removeItem(`customerAlsoBoughtProductGroups/${workTaskId}`)
                return []
            })
        }

        switch (context) {
            case CrossSellingContext.AlsoBoughtArticle:
                if (response && response?.articles.length > 0) {
                    setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, displayButton: false }))
                    setRequest(listId, { articles: response?.articles })
                } else {
                    setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, displayButton: true }))
                }
                break
            case CrossSellingContext.AlsoBoughtProductGroup:
                setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, displayButton: true }))
                break
            default:
                break
        }

        setCustomerAlsoBoughtDetails((prevDetails) => ({
            ...prevDetails,
            articles: response?.articles ?? [],
            productGroups: response?.customerAlsoBoughtProductGroups,
        }))
    }, [
        enableCrossSelling,
        isAlternativeSearch,
        setReferenceArticle,
        article,
        loadArticles,
        context,
        setCustomerAlsoBoughtArticlesChain,
        workTaskId,
        setRecommendedArticlesChain,
        setCustomerAlsoBoughtProductGroupsState,
        setRequest,
    ])

    const loadNextRecommendedArticle = useCallback(
        async (newArticles: Article[]) => {
            if (!enableCrossSelling) {
                return
            }

            setIsLoading(true)

            const response = await loadArticles(newArticles)

            if (!response?.articles?.length) {
                setCustomerAlsoBoughtArticlesChain(() => {
                    localStorage.removeItem(`customerAlsoBought/${workTaskId}`)
                    return []
                })
            }
            if (!response?.recommendedProductGroups?.length) {
                setRecommendedArticlesChain(() => {
                    localStorage.removeItem(`recommendedArticles/${workTaskId}`)
                    return []
                })
            }
            if (!response?.customerAlsoBoughtProductGroups?.length) {
                setCustomerAlsoBoughtProductGroupsState(() => {
                    localStorage.removeItem(`customerAlsoBoughtProductGroups/${workTaskId}`)
                    return []
                })
            }

            switch (context) {
                case CrossSellingContext.AlsoBoughtArticle:
                    if (response && response?.articles?.length > 0) {
                        setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, displayButton: false }))
                        setRequest(listId, { articles: response?.articles })
                    } else {
                        setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, displayButton: true }))
                    }
                    break
                case CrossSellingContext.AlsoBoughtProductGroup:
                    setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, displayButton: true }))
                    break
                default:
                    break
            }

            setCustomerAlsoBoughtDetails((prevDetails) => ({
                ...prevDetails,
                articles: response?.articles,
                productGroups: response?.customerAlsoBoughtProductGroups,
            }))

            setIsLoading(false)
        },
        [
            enableCrossSelling,
            loadArticles,
            context,
            setCustomerAlsoBoughtArticlesChain,
            workTaskId,
            setRecommendedArticlesChain,
            setCustomerAlsoBoughtProductGroupsState,
            setRequest,
        ]
    )

    const onClose = useCallback(() => {
        setCustomerAlsoBoughtDetails((prevDetails) => ({ ...prevDetails, articles: undefined, productGroups: undefined }))
        setProductGroups({ recommendedProductGroupsState: [], customerAlsoBoughtProductGroupsState: [] })
        reset(listId)
    }, [reset, setCustomerAlsoBoughtDetails, setProductGroups])

    const onProductGroupClick = useCallback(
        (productGroup: ProductGroup, isRecommended?: boolean) => {
            if (!productGroup) {
                return
            }

            if (listType === "vehicle") {
                return channel("WORKTASK").publish(
                    "PARTS/REQUEST_LIST",
                    createVehiclePartsRequestPayload(vehicle?.tecDocTypeId!, [productGroup?.id], isRecommended)
                )
            }

            return channel("WORKTASK").publish(
                "PARTS/REQUEST_LIST",
                createUniversalSearchRequestPayload(productGroup?.name, [productGroup?.id], isRecommended)
            )
        },
        [listType, vehicle]
    )

    const displayCustomerAlsoBoughtTab = useMemo(
        () =>
            !!customerAlsoBoughtDetails?.articles?.length ||
            !!productGroups?.customerAlsoBoughtProductGroupsState?.length ||
            !!customerAlsoBoughtDetails.articles?.length,
        [customerAlsoBoughtDetails, productGroups]
    )

    return useMemo(
        () => ({
            isEnabled,
            isLoading,
            loadRecommendedArticles,
            loadNextRecommendedArticle,
            onClose,
            ListComponent: PureList,
            customerAlsoBoughtDetails,
            articles: customerAlsoBoughtDetails?.articles,
            productGroups,
            onProductGroupClick,
            CustomerAlsoBoughtArticlesChain,
            RecommendedArticlesChain,
            displayCustomerAlsoBoughtTab,
        }),
        [
            isEnabled,
            isLoading,
            loadRecommendedArticles,
            loadNextRecommendedArticle,
            onClose,
            customerAlsoBoughtDetails,
            onProductGroupClick,
            productGroups,
            displayCustomerAlsoBoughtTab,
        ]
    )
}
